import { createElement, forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { IntegerInputRef, IntegerInputProps } from './';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { Input } from '../../Input';
import { setRef } from '../../../util/ref-util';
import { useMounted } from '../../../hooks/useMounted';

export const IntegerInput = forwardRef<IntegerInputRef, IntegerInputProps>(function IntegerInput(
  props,
  ref
) {
  const [editValue, setEditValue] = useState(String(props.value));
  /**
   * Possible values:
   * - **unfocused**: the user isn't editing the value
   * - **editing**: the user is editing the value
   * - **committed**: the user just committed a value but is still editing. This state should have stricter
   *   validation than regular editing.
   */
  type EditState = 'committed' | 'editing' | 'unfocused';
  const [editState, setEditState] = useState<EditState>('unfocused');

  const isValid = isValidInteger(editValue, {
    minValue: props.minValue,
    maxValue: props.maxValue,
    isEditing: editState === 'editing',
    isDuration: props.isDuration ?? false,
  });

  const refInput = useRef<HTMLInputElement | null>(null);
  useImperativeHandle(ref, () => ({
    isValid: () => isValid,
    focus: () => refInput.current?.focus(),
  }));

  const localize = useNCTextLocalizer('DatePicker');
  const mountedRef = useMounted();

  useEffect(() => {
    function handleInputKeyDown() {
      setEditState((prevState) => (prevState !== 'committed' ? 'editing' : prevState));
    }

    const input = refInput.current;
    input?.addEventListener('keydown', handleInputKeyDown);
    return function () {
      input?.removeEventListener('keydown', handleInputKeyDown);
    };
  });

  return (
    <Input
      ref={(element) => {
        refInput.current = element;
        setRef(props.inputRef, element);
      }}
      id={props.id}
      testId={props.testId}
      className={props.className}
      style={props.style}
      ariaLabel={props.ariaLabel}
      maxLength={Math.max(String(props.minValue).length, String(props.maxValue).length)}
      value={editState !== 'unfocused' || !isValid ? editValue : String(props.value)}
      status={isValid ? undefined : 'alert'}
      message={
        !isValid
          ? localize(props.invalidMessageKey ?? 'invalidInteger.msg', {
              min: String(props.minValue),
              max: String(props.maxValue),
            })
          : undefined
      }
      onFocus={() => {
        if (isValid) {
          setEditValue(String(props.value));
        }
      }}
      onBlur={() => {
        // NOTE: input uses `useOnFocusOut` when it probably doesn't need to.
        //     | the issue with using that hook is that it causes onCommit to be fired after onBlur.
        // NOTE: it probably doesn't need to use that hook because it's popover probably shouldn't receive focus,
        //     | it also doesn't receive focus in this case because there are no suggestions for this input.
        setTimeout(() => setEditState('unfocused'), 0);
      }}
      onChange={(event) => {
        setEditValue(event.value);
        setEditState((prevState) => (prevState !== 'unfocused' ? 'editing' : prevState));
        if (event.value.length > 0 && (!props.isDuration || isValid)) {
          props.onChange(parseInteger(event.value, props.isDuration ?? false), 'onChange');
        }
      }}
      onCommit={(event) => {
        if (!mountedRef.current) return;
        setEditState((prevState) => (prevState === 'editing' ? 'committed' : prevState));
        const value = parseInteger(event.value, props.isDuration ?? false);
        if (!isNaN(value) && (!props.isDuration || isValid)) {
          props.onChange(value, 'onCommit');
        }
      }}
    />
  );
});

const validIntegerString = /^\s*[0-9]+\s*$/u;
const validDurationIntegerString = /^-?\s*[0-9]+\s*$/u;

function parseInteger(value: string, isDuration: boolean): number {
  if (isDuration) return validDurationIntegerString.test(value) ? Number.parseInt(value, 10) : NaN;
  return validIntegerString.test(value) ? Number.parseInt(value, 10) : NaN;
}

function isValidInteger(
  value: string,
  options: { isEditing: boolean; minValue: number; maxValue: number; isDuration: boolean }
): boolean {
  const parsedValue = parseInteger(value, options.isDuration);

  if (options.isEditing) {
    if (options.isDuration && value.lastIndexOf('-') === 0) return true;
    if (value.includes(' ')) return false;
    const len = value.trim().length;
    const maxLength = Math.max(String(options.minValue).length, String(options.maxValue).length);
    return (
      (!isNaN(parsedValue) || len === 0) && (len < maxLength || inBounds(parsedValue, options))
    );
  } else {
    return !isNaN(parsedValue) && inBounds(parsedValue, options);
  }
}

function inBounds(value: number, options: { minValue: number; maxValue: number }): boolean {
  return options.minValue <= value && value <= options.maxValue;
}
